 
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import {
  RowType,
  TableType,
} from '../../../pages/FinancialForensicsPage/FinancialForensics.types';
import {
  Reconciliation,
  ReconciliationDocument,
} from '../../../pages/ReconciliationWorkspace/ReconciliationWorkspace.types';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { commasForNumbers, formatDate } from '../helper';
import React, { CSSProperties } from 'react';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { FixedCostFeeCalculation } from '../../../pages/FinancialForensicsPage/types/FixedCost.types';
import { calculateFee, UnitType } from '../financialForensicsHelper';
import { calculateSum, getCalculationVariables } from './fixedCostHelper';
import {
  BaseEntry,
  BreakdownEntry,
  EmployeeCount,
} from '../../../pages/FinancialForensicsPage/types/EmployeeCount.types';
import { Csv, Excel, Pdf, Word } from '@HomaHealth/ui-component-library';
import {
  cellStyles3,
  cellStyles4,
  Columns,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';

const useStyles = makeStyles()({
  icon: {
    height: '16px',
    width: '16px',
    margin: '0px 8px 0px 0px',
    '& path': {
      stroke: patientAccessTheme.palette.warning.A600,
    },
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
  },
  underline: {
    '&:hover': {
      color: patientAccessTheme.palette.primary.main + ' !important',
      textDecoration: 'underline dashed',
      textUnderlineOffset: '0.25rem',
      cursor: 'pointer',
    },
  },
});

export const reconciliationDocumentsColumns: Columns[] = [
  {
    name: 'Document Name',
    align: 'left',
    width: '',
    cellStyles: cellStyles3,
    typographyStyles: {
      fontSize: '14px',
    },
  },
  {
    name: 'Reconciliation Source',
    align: 'left',
    width: '',
    cellStyles: cellStyles4,
  },
  {
    name: 'Uploaded By',
    align: 'left',
    width: '',
    cellStyles: cellStyles4,
  },
  {
    name: 'Upload Date',
    align: 'left',
    width: '',
    cellStyles: cellStyles4,
  },
];

export const formatSavedReconciliation = (
  updatedReconciliation: any,
): Reconciliation => {
  const { reconciliationName, notes, _id } = updatedReconciliation;

  return {
    id: _id,
    name: reconciliationName,
    notes,
    documents: [],
  };
};

export const formatDocuments = (
  reconciliations: Reconciliation[],
  handleDocumentToOpen: (fileName: string, url: string) => void,
) => {
  const rowsData: any[] = [];
  const iconStyles = {
    height: '20px',
    width: '20px',
  };

  reconciliations.forEach((item) => {
    if (item.documents.length === 0) {
      return;
    }
    item.documents.map((obj: ReconciliationDocument) => {
      const row = [];
      row.push(
        <Grid style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {getFileIcon(obj.fileType, iconStyles)}
          <Typography
            fontSize="14px"
            fontType="medium"
            textColor={patientAccessTheme.palette.primary.main}
            styleProps={{
              textDecoration: 'underline dashed',
              textUnderlineOffset: '6px',
              cursor: 'pointer',
            }}
            onClick={() => handleDocumentToOpen(obj.fileName, obj.fileUrl)}
          >
            {obj.fileName}
          </Typography>
        </Grid>,
      );
      row.push(item.name);
      row.push(obj.uploadedBy);
      row.push(formatDate(obj.uploadedAt, 'YYYY-MM-DD'));
      rowsData.push(row);
    });
  });

  return rowsData;
};

export const mapFixedType = (rowType: RowType) => {
  if (rowType === 'member') {
    return 'MEMBER';
  }
  if (rowType === 'primary') {
    return 'FEES';
  }
  return 'CREDITS';
};

export const computeUpdatedFixedCost = (
  prev: any,
  vendorId: string,
  serviceId: string,
  reconciliationName: string,
  updatedCalculation: FixedCostFeeCalculation,
) => {
  let updatedFee = 0;

  const updateServices = (items: any[]) =>
    items?.map((item: any) => {
      if (item.vendorID !== vendorId) {
        return item;
      }

      const updatedServices = item?.services?.map((service: any) => {
        if (service.serviceID !== serviceId) {
          return service;
        }

        if (updatedCalculation.option === 'Enter Total Fee') {
          updatedFee = calculateSum(updatedCalculation.details);
        }

        if (updatedCalculation.option === 'Enter Breakdown') {
          const calculationVariables = getCalculationVariables(
            service.unitOfFees as UnitType,
            {
              monthCount:
                updatedCalculation.detailsType === 'Annually' ? 12 : 1,
              yearCount: 1,
            },
            updatedCalculation.details,
          );

          updatedFee = calculateFee(
            service.unitOfFees as UnitType,
            service.value,
            calculationVariables,
          );
        }

        return {
          ...service,
          [reconciliationName]: {
            ...service[reconciliationName],
            fees: updatedFee,
            feeCalculation: updatedCalculation,
            isChosen: true,
          },
        };
      });

      return { ...item, services: updatedServices };
    });

  return {
    updatedState: {
      planContribution: {
        fees: updateServices(prev?.planContribution?.fees ?? []),
        credits: updateServices(prev?.planContribution?.credits ?? []),
        stoploss: [],
      },
      memberContribution: updateServices(prev?.memberContribution ?? []),
    },
    updatedFee,
  };
};
export const computeUpdatedVariableCost = (
  prev: any,
  vendorId: string,
  reconciliationName: string,
  data: any,
  target: 'FEES' | 'REBATES',
) => {
  const updatedFee = calculateSum(data?.feeCalculation?.details ?? []);

  const updateVendors = (items: any[]) =>
    items?.map((item: any) =>
      item.vendorID === vendorId
        ? {
            ...item,
            [reconciliationName]: {
              ...item[reconciliationName],
              value: updatedFee,
              feeCalculation: data.feeCalculation,
              isChosen: true,
            },
          }
        : item,
    );

  const updatedState = {
    ...prev,
    planContribution: {
      rebates:
        target === 'REBATES'
          ? updateVendors(prev?.planContribution?.rebates ?? [])
          : prev?.planContribution?.rebates,
      fees:
        target === 'FEES'
          ? updateVendors(prev?.planContribution?.fees ?? [])
          : prev?.planContribution?.fees,
    },
    memberContribution:
      target === 'FEES'
        ? updateVendors(prev?.memberContribution ?? [])
        : prev?.memberContribution,
  };

  return { updatedState, updatedFee };
};

export const updateEmployeeCountWithReconciliation = (
  prevEmployeeCount: EmployeeCount,
  reconciliationEmployeeCount: EmployeeCount,
  reconciliationName: string,
): EmployeeCount => {
  const mergeBaseEntry = (
    prev: BaseEntry,
    reconciliation: BaseEntry,
  ): BaseEntry => ({
    ...prev,
    [reconciliationName]: reconciliation,
  });

  const mergeBreakdownEntry = (
    prev: BreakdownEntry,
    reconciliation: BreakdownEntry,
  ): BreakdownEntry => ({
    ...prev,
    [reconciliationName]: reconciliation,
  });

  return {
    employees: mergeBaseEntry(
      prevEmployeeCount.employees,
      reconciliationEmployeeCount?.employees,
    ),
    members: mergeBaseEntry(
      prevEmployeeCount.members,
      reconciliationEmployeeCount?.members,
    ),
    membersBySubscriptionType: mergeBreakdownEntry(
      prevEmployeeCount.membersBySubscriptionType,
      reconciliationEmployeeCount?.membersBySubscriptionType,
    ),
    employeeBySubscriptionType: mergeBreakdownEntry(
      prevEmployeeCount.employeeBySubscriptionType,
      reconciliationEmployeeCount?.employeeBySubscriptionType,
    ),
    employeeByPlanType: mergeBreakdownEntry(
      prevEmployeeCount.employeeByPlanType,
      reconciliationEmployeeCount?.employeeByPlanType,
    ),
    memberByPlanType: mergeBreakdownEntry(
      prevEmployeeCount.memberByPlanType,
      reconciliationEmployeeCount?.memberByPlanType,
    ),
  };
};

export const updateVendorsWithReconciliation = (
  prevVendors: any[] = [],
  scenarioVendors: any[] = [],
  reconciliationName: string,
  reconciliationId: string,
  isFixed: boolean,
) => {
  return prevVendors.map((prevVendor) => {
    const matchingVendor = scenarioVendors.find((bVendor: any) => {
      return bVendor.vendorID === prevVendor.vendorID;
    });

    if (!matchingVendor) {
      return prevVendor;
    }

    if (isFixed) {
      const updatedServices = prevVendor.services?.map((prevService: any) => {
        const matchingService = matchingVendor.services?.find(
           
          (bService: any) => bService.serviceID === prevService.serviceID,
        );

        return matchingService
          ? {
              ...prevService,
              [reconciliationName]: {
                ...matchingService,
                id: reconciliationId,
              },
            }
          : prevService;
      });

      return {
        ...prevVendor,
        services: updatedServices,
      };
    }

    return {
      ...prevVendor,
      [reconciliationName]: {
        ...matchingVendor,
        id: reconciliationId,
      },
    };
  });
};

export const AddReconciliation: React.FC<{
  openModalByContext: any;
  row?: any;
  cellValue?: any;
  tableType?: TableType;
}> = ({ openModalByContext, cellValue, tableType }) => {
  const { classes } = useStyles();
  const key = tableType === 'FIXED' ? 'fees' : 'value';
  const displayValue = cellValue?.[key];

  return displayValue !== 0 && displayValue !== undefined ? (
    <Typography
      fontSize="14px"
      fontType="regular"
      textColor={patientAccessTheme.palette.text.mediumemphasis}
      className={classes.underline}
      onClick={openModalByContext}
    >
      ${commasForNumbers(displayValue?.toString() || '0', 2)}
    </Typography>
  ) : (
    <Typography
      fontSize="14px"
      fontType="semiBold"
      textColor={patientAccessTheme.palette.primary[400]}
      styleProps={{
        textDecoration: '1.5px underline dashed',
        textUnderlineOffset: '6px',
        cursor: 'pointer',
      }}
      onClick={openModalByContext}
    >
      Add Reconciliation
    </Typography>
  );
};

export const buildReconciliationColumn = (
  reconciliation: { id: string; name: string },
  tableType: TableType,
  openModalByContext: (
    tableType: TableType,
    rowType: RowType,
    row: any,
    vendor: any,
    col: any,
  ) => void,
) => ({
  name: reconciliation.name,
  accessor: reconciliation.name,
  width: '',
  cell: (value: any, row?: any, rowType?: RowType, parentItem?: any) => {
    return (
      <AddReconciliation
        openModalByContext={() =>
          openModalByContext(tableType, rowType as RowType, row, parentItem, {
            name: reconciliation.name,
          })
        }
        row={row}
        cellValue={row?.[reconciliation.name]}
        tableType={tableType}
      />
    );
  },
});

export const getFileIcon = (type: string, iconStyles?: CSSProperties) => {
  switch (type) {
    case 'application/pdf':
      return <Pdf style={iconStyles} />;
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return <Word style={iconStyles} />;
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return <Excel style={iconStyles} />;
    case 'text/csv':
      return <Csv style={iconStyles} />;
    default:
      return <Pdf style={iconStyles} />;
  }
};
